import type {
  TenantTool,
  ToolSettingsDefault,
  ToolSettingsOverride,
} from "@repo/components/ctrl/hooks";

export const CHANNEL_OPTIONS = [
  { value: "stable", label: "Stable" },
  { value: "beta", label: "Beta" },
  { value: "edge", label: "Edge" },
];

export const POLICY_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "notify", label: "Notify" },
  { value: "manual", label: "Manual" },
];

export const POLICY_DESCRIPTIONS: Record<string, string> = {
  auto: "Adopt new releases on the channel automatically.",
  notify: "Flag new releases here and in the CLI; adopt them yourself.",
  manual: "Never prompt; releases are adopted only when you ask.",
};

export const DEFAULT_TOOL_SETTINGS: Required<ToolSettingsDefault> = {
  channel: "stable",
  upgrade_policy: "notify",
  mirror: true,
  hold_days: 0,
};

/** Compare two semver strings; prereleases sort before their release. */
export function compareVersions(a: string, b: string): number {
  const parse = (v: string) => {
    const [core = "", pre] = v.replace(/^v/, "").split("-", 2);
    return { nums: core.split(".").map((n) => Number.parseInt(n, 10) || 0), pre };
  };
  const pa = parse(a);
  const pb = parse(b);
  for (let i = 0; i < Math.max(pa.nums.length, pb.nums.length); i++) {
    const diff = (pa.nums[i] ?? 0) - (pb.nums[i] ?? 0);
    if (diff !== 0) return diff;
  }
  if (pa.pre === pb.pre) return 0;
  if (pa.pre === undefined) return 1;
  if (pb.pre === undefined) return -1;
  return pa.pre < pb.pre ? -1 : 1;
}

export type ToolStatusView = {
  label: string;
  color: string;
  /** Available is older than adopted: adopting it rolls back. */
  rollback: boolean;
};

export function describeStatus(tool: TenantTool): ToolStatusView {
  const rollback =
    tool.status === "update_available" &&
    Boolean(tool.adopted_version) &&
    Boolean(tool.available_version) &&
    compareVersions(tool.available_version!, tool.adopted_version!) < 0;

  if (rollback) return { label: "Rollback available", color: "orange", rollback };
  switch (tool.status) {
    case "current":
      return { label: "Current", color: "green", rollback };
    case "update_available":
      return { label: "Update available", color: "blue", rollback };
    case "incompatible":
      return { label: "Incompatible", color: "red", rollback };
    case "pinned":
      return { label: "Pinned", color: "grape", rollback };
    case "ahead_of_channel":
      return { label: "Ahead of channel", color: "cyan", rollback };
    default:
      return { label: tool.status ?? "Unknown", color: "gray", rollback };
  }
}

/** Why adopting is unavailable for this tool, or null when it is allowed. */
export function adoptDisabledReason(tool: TenantTool): string | null {
  if (tool.status === "pinned") {
    return `Pinned to ${tool.effective?.pinned_version ?? "a version"}. Clear the pin to adopt.`;
  }
  if (tool.status === "ahead_of_channel") return AHEAD_HELP;
  if (tool.status === "incompatible") {
    return "The available release is incompatible with other adopted tools.";
  }
  if (!tool.available_version) return "No release is available on this channel.";
  if (tool.status === "current") return "Already on the available version.";
  return null;
}

/** `0.1.509+sandbox.9fe3` -> `["0.1.509", "sandbox.9fe3"]`; build is `""` when absent. */
export function splitBuild(version: string): [string, string] {
  const i = version.indexOf("+");
  return i < 0 ? [version, ""] : [version.slice(0, i), version.slice(i + 1)];
}

/** `registry/path@sha256:0123456789ab` -> `sha256:0123456789ab…` */
export function shortDigest(reference: string): string {
  const digest = reference.includes("@") ? reference.split("@").pop()! : reference;
  const [algo, hex] = digest.split(":");
  if (!hex) return digest.length > 19 ? `${digest.slice(0, 19)}…` : digest;
  return `${algo}:${hex.slice(0, 12)}…`;
}

export function formatTime(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

export function isEmptyOverride(o: ToolSettingsOverride): boolean {
  return (
    o.channel == null &&
    o.upgrade_policy == null &&
    o.mirror == null &&
    !o.pinned_version
  );
}

/** Extract a human message from a CtrlApiError-like value. */
export function errorMessage(error: unknown): string {
  const e = error as { data?: { message?: string }; message?: string } | null;
  return e?.data?.message ?? e?.message ?? "Unknown error";
}

export function isNotConfigured(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 503;
}

/** 409 from tools listing/adopt when the tenant has no Keycloak organisation. */
export function isMissingOrganisation(error: unknown): boolean {
  const e = error as { status?: number; data?: { message?: string }; message?: string } | null;
  if (e?.status !== 409) return false;
  return /no organisation id/i.test(e.data?.message ?? e.message ?? "");
}

export type KindFilter = "all" | "cli" | "plugin" | "image";

export const KIND_FILTERS: { value: KindFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "cli", label: "CLI" },
  { value: "plugin", label: "Plugins" },
  { value: "image", label: "Images" },
];

export const KIND_LABEL: Record<string, string> = {
  cli: "CLI",
  plugin: "Plugin",
  image: "Image",
};

export const kindLabel = (kind?: string) => (kind ? (KIND_LABEL[kind] ?? kind) : "—");

/** "Images" also covers CLI tools whose adopted release ships an image (nullctl). */
export const matchesKind = (tool: { kind?: string; adopted_digest?: string }, filter: KindFilter) =>
  filter === "all" || tool.kind === filter || (filter === "image" && Boolean(tool.adopted_digest));

/** Route of a tool's detail page. */
export const toolPath = (name: string) => `/org/tools/${encodeURIComponent(name)}`;

/** `nullctl-infra` -> `infra`: plugins install by their short name. */
export const pluginShortName = (tool: string) => tool.replace(/^nullctl-/, "");

export const pluginInstallCommand = (tool: string) =>
  `nullctl tools plugins install ${pluginShortName(tool)}`;

/** Person behind an adoption: resolved name/email, else the raw actor (reconciler, pin). */
export function adoptedByLabel(tool: TenantTool): string {
  const u = tool.adopted_by_user;
  if (u) return u.name || u.email || u.id || tool.adopted_by || "—";
  return tool.adopted_by || "—";
}

export const AHEAD_HELP =
  "The adopted version is newer than the channel head. Nothing to do; it stays until the channel catches up.";

export type CliCommand = { label: string; command: string };

/** Commands the user runs on their own machine for this tool and state. */
export function cliCommands(tool: TenantTool): CliCommand[] {
  const name = tool.tool ?? "";
  const out: CliCommand[] = [{ label: "Check status", command: "nullctl tools status" }];
  if (tool.kind === "plugin") {
    out.push({
      label: "Install this plugin",
      command: pluginInstallCommand(name),
    });
    out.push({
      label: "Update installed plugins",
      command: "nullctl tools plugins update",
    });
  } else if (tool.kind === "cli") {
    out.push({ label: "Update nullctl", command: "nullctl tools update" });
  }
  const target = tool.available_version || tool.adopted_version;
  if (target) {
    out.push({ label: "Adopt a version (admins)", command: `nullctl tools adopt ${name} ${target}` });
  }
  out.push({
    label: "List releases",
    command: `nullctl tools releases ${name}`,
  });
  return out;
}
